import { useDispatch, useSelector } from 'react-redux'
import { selectPastOptionExpiriesV2, selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button, Menu, MenuItem, Typography } from '@mui/material'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { fetchSecuritySnapshotsBulk } from '../../../../services/apis/candles'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../../components/custom'
import { ChartContext } from '../../../../components/chartV2/chart.context'
import { OptionType as EnumOptionType, optionTypeToJSON } from '../../../../proto/generated/security'
import { OptionType, setDropdownPastOptionExpiries } from '../../../../reducer/options.reducer'
import { fetchOptionExpiriesV2 } from '../../../../util/options'
import { getOptionPastExpiries } from '../../../../services/apis/optionChain'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import { ChartViewKey, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import { Security } from '../../../../proto/generated/candle-data-depot-common'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../../../assets/routes'
import { WidgetTypes } from '../../../../components/widgetDashboard/widgetDashboard.enums'
import { selectActiveChartId, selectAtmOptionConfigChartSecurityKey } from '../../../../selectors/chart'
import { selectActiveLayoutId, selectPrimarySecurity } from '../../../../selectors/home'
import { selectActiveWidget } from '../../../../selectors/widgetDashboard'
import { updateAtmOptionChart } from '../../../../reducer/chart.reducer'
import { selectIsEmbedded } from '../../../../selectors/device.ts'
import { callNative } from '../../../../util/nativeBridge'

export type OptionStrikeButtonProps = {
    option: Security
    selected: boolean
    selectedOptionType: OptionType['optionType']
    optionType: OptionType['optionType']
    onClick: (option: Security) => void
}

export const OptionStrikeButton = memo(({ selected, option, selectedOptionType, optionType, onClick }: OptionStrikeButtonProps) => {
    return (
        <Button
            variant={selected && selectedOptionType === optionType ? 'contained' : 'outlined'}
            onClick={() => {
                onClick(option)
            }}
            color={optionType === 'CE' ? 'call' : 'put'}
            sx={{
                py: 0
            }}
        >
            {optionType}
        </Button>
    )
})

const OptionsChartStrikeMenu: React.FC<{ chartSecurityKey?: string }> = ({ chartSecurityKey }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const setPrimarySecurity = useSetPrimarySecurity()
    const chartContext = useContext(ChartContext)
    const activeChartId = chartContext?.viewKey
    const chartSecurities = chartContext?.chart?.securities
    const underlyingToken = chartSecurities?.length ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode ?? '') : ''
    const [underlying, setUnderlying] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (underlyingToken) getSecurityAsync(underlyingToken).then(setUnderlying); else setUnderlying(undefined) }, [underlyingToken])
    const { key: primarySecurityKey } = useSelector(selectPrimarySecurity)
    const atmOptionSecurityKey = useSelector(selectAtmOptionConfigChartSecurityKey(activeChartId ?? ''))
    const contextChartKey = chartSecurities?.length
        ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code)
        : ''
    const key = atmOptionSecurityKey || chartSecurityKey || contextChartKey || primarySecurityKey
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken))
    const pastExpiries = useSelector(selectPastOptionExpiriesV2(underlyingToken))
    const [optionStrikeMenuAnchorEl, setOptionStrikeMenuAnchorEl] = useState(null)
    const [selectedChartSecurity, setSelectedChartSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (key) getSecurityAsync(key).then(setSelectedChartSecurity); else setSelectedChartSecurity(undefined) }, [key])
    const [roundingStrikesRequired, setRoundingStrikesRequired] = useState(false)
    const viewKey = useSelector(selectActiveChartId)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const isEmbedded = useSelector(selectIsEmbedded)

    useEffect(() => {
        const price = (selectedChartSecurity?.strikePrice ?? 0) / (selectedChartSecurity?.priceMultiplier ?? 100)
        if (!Number.isInteger(price)) {
            setRoundingStrikesRequired(true)
        } else {
            setRoundingStrikesRequired(false)
        }
    }, [selectedChartSecurity])

    const selectedExpiry = useMemo(() => {
        if (optionsExpiries) {
            const expiry = selectedChartSecurity?.expiry
            if (expiry) {
                const index = optionsExpiries.findIndex((o) => o.expiry.expiry === expiry.expiry)
                if (index !== -1) return optionsExpiries[index]
            }
        }
    }, [selectedChartSecurity, optionsExpiries])

    const fetchOptionsPastExpiries = useCallback(() => {
        if (!underlying) return
        getOptionPastExpiries({
            exchange: underlying.exchange,
            securityCode: underlying.code
        }).then((response) => {
            dispatch(
                setDropdownPastOptionExpiries({
                    token: underlyingToken,
                    optionExpiries: response
                })
            )
        })
    }, [dispatch, underlying, underlyingToken])

    const selectedPastExpiry = useMemo(() => {
        if (!pastExpiries) return
        const expiry = selectedChartSecurity?.expiry
        if (expiry) {
            const index = pastExpiries.findIndex((o) => o.expiry.expiry === expiry.expiry)
            if (index !== -1) return pastExpiries[index]
        }
    }, [selectedChartSecurity, pastExpiries])

    const handleOptionStrikeMenuClick = useCallback(
        (event: any) => {
            if (isEmbedded) {
                if (window.isEmbeddedMode) {
                    const strike = selectedChartSecurity?.strikePrice || 0
                    const securityKey = key || ''
                    const metaData = {
                        type: 'strike',
                        strike: strike,
                        securityKey: securityKey,
                        timestamp: Date.now()
                    }
                    callNative('onScalperSeriesMetaDataClicked', JSON.stringify(metaData))
                } else {
                    callNative('onSeriesMetaDataStrikeClicked')
                }
            } else {
                if (!optionsExpiries?.length) {
                    const securityToFetch = selectedChartSecurity?.hasFo ? selectedChartSecurity : underlying
                    fetchOptionExpiriesV2(securityToFetch, dispatch)
                }
                if (selectedChartSecurity?.expiry?.expiry && selectedChartSecurity?.expiry?.expiry < new Date().getTime() && !pastExpiries) {
                    fetchOptionsPastExpiries()
                }
                setOptionStrikeMenuAnchorEl(event.currentTarget)
            }
        },
        [dispatch, fetchOptionsPastExpiries, optionsExpiries?.length, pastExpiries, selectedChartSecurity, underlying, key]
    )

    const handleOptionStrikeMenuClose = useCallback(() => {
        setOptionStrikeMenuAnchorEl(null)
    }, [])

    const handleOptionChange = useCallback(
        async (security: Security) => {
            if (!security) return
            const key = getSecurityKey(security.exchange, security.code)
            let sec = await getSecurityAsync(key)
            if (!sec) {
                try {
                    const res = await fetchSecuritySnapshotsBulk({
                        securities: [
                            {
                                exchange: security.exchange,
                                code: security.code
                            }
                        ],
                        dispatch
                    })
                    const [sn] = res.securitySnapshots
                    if (sn) {
                        sec = sn.security!
                    }
                } catch (e) {
                    console.error(e)
                }
            }
            if (sec) {
                setSelectedChartSecurity(sec)
                setTimeout(() => {
                    if (activeChartId === ChartViewKey.SYMBOL) {
                        navigate(generatePath(ROUTES.symbol, { symbol: getSecurityKey(sec.exchange, sec.code) }))
                        return
                    }
                    if (activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART) {
                        const configChartKey = selectedChartSecurity
                            ? getSecurityKey(selectedChartSecurity.exchange, selectedChartSecurity.code)
                            : key
                        dispatch(
                            updateAtmOptionChart({
                                viewKey,
                                config: { key: configChartKey, securityKey: getSecurityKey(sec.exchange, sec.code) },
                                createIfAbsent: true
                            })
                        )
                    } else {
                        setPrimarySecurity(key, [sec], false)
                    }
                }, 0)
            }
            AnalyticsManager.logEvent('Options Price Charts Strike Changed', { selected_value: security.code })
        },
        [dispatch, setPrimarySecurity, activeChartId, activeWidget?.widgetType, selectedChartSecurity, key, viewKey, navigate]
    )

    const strikes = useMemo(() => {
        if (!selectedChartSecurity) return []
        if (selectedChartSecurity?.derived) {
            return [
                {
                    strike: 0,
                    callSecurity: {
                        exchange: selectedChartSecurity?.exchange,
                        code: selectedChartSecurity?.code.replace(/_(CE|PE)$/, '') + '_CE'
                    },
                    putSecurity: {
                        exchange: selectedChartSecurity?.exchange,
                        code: selectedChartSecurity?.code.replace(/_(CE|PE)$/, '') + '_PE'
                    }
                }
            ];
        }
        selectedExpiry?.strikes.forEach((s) => {
            const price = s.strike / (underlying?.priceMultiplier ?? 100)
            if (!Number.isInteger(price) && !roundingStrikesRequired) setRoundingStrikesRequired(true)
        })
        return selectedExpiry?.strikes || selectedPastExpiry?.strikes || []
    }, [roundingStrikesRequired, selectedChartSecurity, selectedExpiry, selectedPastExpiry, underlying])

    const jsonSelectedOptionType = useMemo(() => {
        return optionTypeToJSON(selectedChartSecurity?.optionType ?? EnumOptionType.CE)
    }, [selectedChartSecurity?.optionType])
    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <>
            <NonInteractiveTooltip placement={'top'} title={`${selectedChartSecurity?.derived ? 'Change Option Type' : 'Change Strike'}`} arrow>
                <ExtraSmallButton size="small" variant="text" onClick={handleOptionStrikeMenuClick} color="inherit" sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', fontSize: '14px' }}>
                    {selectedChartSecurity
                        ? `${!!selectedChartSecurity?.strikePrice ? (selectedChartSecurity?.strikePrice / (selectedChartSecurity?.priceMultiplier || 100)).toFixed(roundingStrikesRequired ? 2 : 0) + ' ' : ''}${jsonSelectedOptionType === 'CE' ? 'CE' : 'PE'}`
                        : ''}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            {Boolean(optionStrikeMenuAnchorEl) && !isEmbedded && (
                <Menu
                    open={Boolean(optionStrikeMenuAnchorEl)}
                    anchorEl={optionStrikeMenuAnchorEl}
                    autoFocus={true}
                    onClose={handleOptionStrikeMenuClose}
                    container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    onClick={handleOptionStrikeMenuClose}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                >
                    {strikes.map((str) => {
                        const selected = str.strike === selectedChartSecurity?.strikePrice
                        return (
                            <MenuItem
                                key={str.strike}
                                disableRipple
                                selected={selected}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gap: 1,
                                    px: 1,
                                    py: 0.5,
                                    '&.Mui-selected': {
                                        backgroundColor: 'transparent !important' // Optional: Change selected background color
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent !important'
                                    }
                                }}
                            >
                                {str.callSecurity && (
                                    <OptionStrikeButton
                                        selected={selected}
                                        option={str.callSecurity}
                                        optionType="CE"
                                        selectedOptionType={jsonSelectedOptionType as OptionType['optionType']}
                                        onClick={handleOptionChange}
                                    />
                                )}
                                {!!str.strike && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                            fontWeight: selected ? 500 : 400,
                                            textAlign: 'center'
                                        }}
                                    >
                                        {`${selectedChartSecurity ? (str.strike / (selectedChartSecurity?.priceMultiplier || 100)).toFixed(0) : ''}`}
                                    </Typography>
                                )}
                                {str.putSecurity && (
                                    <OptionStrikeButton
                                        selected={selected}
                                        option={str.putSecurity}
                                        optionType="PE"
                                        selectedOptionType={jsonSelectedOptionType as OptionType['optionType']}
                                        onClick={handleOptionChange}
                                    />
                                )}
                            </MenuItem>
                        )
                    })}
                </Menu>
            )}
        </>
    )
}

export default memo(OptionsChartStrikeMenu)
